import { hasModifierKey } from '@angular/cdk/keycodes';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Action } from '@ngrx/store';
import { UnsavedChangesDialogComponent } from '../components/unsaved-changes-dialog/unsaved-changes-dialog.component';

export const UNSAVED_CHANGES_DIALOG_ID = 'unsaved-changes-dialog';

/**
 * Opens the shared unsaved-changes confirmation, or returns the ref of the one already open.
 *
 * Both the `confirmCancel` effect and the `unsavedChangesGuard` flow open this dialog under a
 * single id, and the two can race: a popstate (browser back) navigation still runs route guards
 * while an action-driven confirmation is on screen, and `MatDialog.open` throws on a duplicate
 * id. Reusing the open ref keeps one confirmation on screen instead of erroring the navigation.
 *
 * `afterClosed()` resolves `true` only for the explicit Discard button; Cancel and Escape close
 * with `undefined`, so guard callers mapping `result === true` fail closed (stay on the page).
 */
export function openUnsavedChangesDialog(
  dialog: MatDialog,
  continueAction: Action | null
): MatDialogRef<UnsavedChangesDialogComponent, boolean> {
  const existing = dialog.getDialogById(UNSAVED_CHANGES_DIALOG_ID);
  if (existing) {
    return existing as MatDialogRef<UnsavedChangesDialogComponent, boolean>;
  }

  const dialogRef = dialog.open<UnsavedChangesDialogComponent, { continueAction: Action | null }, boolean>(
    UnsavedChangesDialogComponent,
    {
      id: UNSAVED_CHANGES_DIALOG_ID,
      data: { continueAction },
      disableClose: true,
      autoFocus: false,
      position: { top: '150px' },
      width: '400px',
      minHeight: '200px',
      panelClass: 'dialog',
    }
  );
  closeOnEscape(dialogRef);
  return dialogRef;
}

/**
 * Restores Escape-to-close for a dialog opened with `disableClose: true`.
 *
 * `disableClose` suppresses BOTH the Escape key and backdrop-click dismissal. Feature
 * dialogs set it to guard against an accidental outside-click discarding in-progress work,
 * but a modal that can't be dismissed with Escape is a keyboard accessibility gap (the ARIA
 * dialog pattern / WCAG 2.1.1 Keyboard). This subscribes to the dialog's keydown stream and
 * handles Escape only, leaving backdrop-click dismissal disabled.
 *
 * By default Escape calls `dialogRef.close(closeResult)`. `closeResult` is forwarded so callers
 * whose `afterClosed()` handler branches on the result (e.g. the idle-warning dialog, where a
 * truthy result keeps the session alive) can make Escape resolve to the affirmative action
 * rather than a bare dismiss. It defaults to `undefined`, matching a plain `close()`.
 *
 * `onEscape` overrides the default entirely: when provided, Escape invokes it instead of closing
 * the dialog. Use this when Escape must mirror a guarded Cancel button rather than force-close —
 * e.g. the schedule-appointment dialog routes cancellation through an unsaved-changes
 * confirmation before discarding an in-progress form.
 *
 * The Escape/modifier check mirrors Angular Material's own behavior when `disableClose`
 * is false, so ESC handling matches dialogs that opt in the default way. The keydown stream
 * completes when the dialog closes, so the subscription needs no manual teardown.
 */
export function closeOnEscape<T, R>(
  dialogRef: MatDialogRef<T, R>,
  options: { closeResult?: R; onEscape?: () => void } = {}
): void {
  dialogRef.keydownEvents().subscribe(event => {
    if (event.key === 'Escape' && !hasModifierKey(event)) {
      event.preventDefault();
      if (options.onEscape) {
        options.onEscape();
      } else {
        dialogRef.close(options.closeResult);
      }
    }
  });
}
